import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileSignature, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";

export default function WhatsAppInboxComposer({
    konusmaKey,
    konusma,
    yanitMetin,
    setYanitMetin,
    onSend,
    busy,
    aiTaslaklar,
    aiLoading,
    onLoadAkil,
}) {
    const [sablonlar, setSablonlar] = useState([]);
    const [sablonAcik, setSablonAcik] = useState(false);
    const [onizleme, setOnizleme] = useState("");
    const [onizlemeLoading, setOnizlemeLoading] = useState(false);
    const [vekaletBusy, setVekaletBusy] = useState(false);

    useEffect(() => {
        whatsappMerkez.sablonlar().then((d) => setSablonlar(d.sablonlar || d || [])).catch(() => {});
    }, []);

    const filtreliSablonlar = useMemo(() => {
        const q = yanitMetin.startsWith("/") ? yanitMetin.slice(1).toLowerCase() : "";
        if (!q) return sablonlar.slice(0, 8);
        return sablonlar.filter(
            (s) =>
                (s.kod || "").toLowerCase().includes(q) ||
                (s.ad || "").toLowerCase().includes(q),
        ).slice(0, 8);
    }, [sablonlar, yanitMetin]);

    const sablonModu = yanitMetin.startsWith("/");

    const sablonSec = useCallback(
        async (kod) => {
            if (!konusmaKey) return;
            setOnizlemeLoading(true);
            try {
                const res = await whatsappMerkez.inboxSablonOnizle({
                    sablon_kod: kod,
                    muvekkil_id: konusma?.muvekkil_id,
                    dosya_id: konusma?.konusma_baglantisi?.varsayilan_dosya_id,
                });
                if (res?.ok && res.metin) {
                    setYanitMetin(res.metin);
                    setOnizleme(res.metin);
                    setSablonAcik(false);
                } else {
                    toast.error(res?.detail || "Şablon yüklenemedi.");
                }
            } catch {
                toast.error("Şablon önizleme hatası.");
            } finally {
                setOnizlemeLoading(false);
            }
        },
        [konusma, konusmaKey, setYanitMetin],
    );

    function handleKeyDown(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            onSend?.();
        }
        if (e.key === "Escape") setSablonAcik(false);
    }

    async function vekaletGonder() {
        if (!konusmaKey || vekaletBusy) return;
        setVekaletBusy(true);
        try {
            const res = await whatsappMerkez.inboxVekaletGonder(konusmaKey);
            if (res?.ok) toast.success("Vekalet örneği PDF gönderildi.");
            else toast.error(res?.detail || "Vekalet gönderilemedi.");
        } catch {
            toast.error("Vekalet gönderilemedi.");
        } finally {
            setVekaletBusy(false);
        }
    }

    return (
        <div className="wa-composer">
            {(sablonModu || sablonAcik) && filtreliSablonlar.length > 0 && (
                <div className="wa-composer__sablon-list">
                    {filtreliSablonlar.map((s) => (
                        <button
                            key={s.id || s.kod}
                            type="button"
                            onClick={() => sablonSec(s.kod)}
                            disabled={onizlemeLoading}
                        >
                            <span className="wa-composer__sablon-kod">/{s.kod}</span>
                            <span>{s.ad || s.kod}</span>
                        </button>
                    ))}
                </div>
            )}

            {aiTaslaklar?.length > 0 && (
                <div className="wa-composer__ai">
                    {aiTaslaklar.slice(0, 3).map((t, i) => (
                        <button
                            key={i}
                            type="button"
                            className="wa-composer__ai-chip"
                            onClick={() => setYanitMetin(t.metin || t.text || "")}
                        >
                            <Sparkles className="h-3 w-3" />
                            {(t.metin || t.text || "").slice(0, 72)}…
                        </button>
                    ))}
                </div>
            )}

            <div className="wa-composer__row">
                <button
                    type="button"
                    className="wa-composer__slash"
                    onClick={() => setSablonAcik((v) => !v)}
                    title="Şablon (/ ile ara)"
                >
                    /
                </button>
                <textarea
                    rows={2}
                    value={yanitMetin}
                    onChange={(e) => setYanitMetin(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Yanıt yazın… (/ şablon, Ctrl+Enter gönder)"
                    disabled={busy}
                />
                <button
                    type="button"
                    className="wa-composer__send"
                    onClick={onSend}
                    disabled={busy || !yanitMetin.trim()}
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}
                </button>
            </div>

            <div className="wa-composer__hint">
                <button type="button" onClick={onLoadAkil} disabled={aiLoading}>
                    {aiLoading ? "Akıl…" : "Akıl taslak"}
                </button>
                <button
                    type="button"
                    className="wa-composer__vekalet"
                    onClick={vekaletGonder}
                    disabled={!konusmaKey || vekaletBusy || busy}
                    title="Kaza vekalet örneği PDF + bilgi mesajı gönder"
                >
                    {vekaletBusy ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <FileSignature className="h-3 w-3" />
                    )}
                    Vekalet gönder
                </button>
                {onizlemeLoading ? <span>Şablon yükleniyor…</span> : null}
            </div>
        </div>
    );
}
